import { useEffect, useMemo, useState } from "react";
import { FiDollarSign } from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const number = (value) => Number(value || 0);
const money = (value) =>
  `₹${number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

export default function PayrollPreviewAdvanceRecoverySetup({
  items,
  accessToken,
  recoveries,
  onChange,
}) {
  const [openAdvances, setOpenAdvances] = useState({});
  const [error, setError] = useState("");
  const rows = useMemo(
    () =>
      (items || [])
        .map((item) => ({
          id: item.employee?.id,
          name: item.employee?.fullName,
          code: item.employee?.employeeCode,
          netPayable: item.netPayable,
        }))
        .filter((item) => item.id),
    [items],
  );

  useEffect(() => {
    if (!accessToken || !rows.length) return;
    let active = true;
    Promise.all(
      rows.map(async (row) => {
        const result = await apiRequest(
          `/employee-advances/employee/${row.id}`,
          { accessToken },
        );
        return [
          row.id,
          (Array.isArray(result) ? result : result?.data || []).filter(
            (advance) => advance.status === "OPEN",
          ),
        ];
      }),
    )
      .then((result) => {
        if (active) setOpenAdvances(Object.fromEntries(result));
      })
      .catch((requestError) => {
        if (active)
          setError(requestError.message || "Unable to load employee advances.");
      });
    return () => {
      active = false;
    };
  }, [accessToken, rows.length, rows.map((row) => row.id).join(",")]);

  const setValue = (employeeId, value) =>
    onChange({ ...recoveries, [employeeId]: value });

  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-primary-100 bg-white shadow-sm">
      <div className="flex gap-4 bg-primary-50 px-6 py-5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-100 text-primary-800">
          <FiDollarSign />
        </span>
        <div>
          <h3 className="font-semibold text-gray-950">
            Recover employee advances in this payroll
          </h3>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Optional. Select one open advance per employee. The selected amount
            will be deducted when you generate the draft and recorded as a
            repayment only when payroll is posted.
          </p>
        </div>
      </div>
      {error ? (
        <p className="mx-6 mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      <div className="divide-y divide-gray-100">
        {rows.map((row) => {
          const choices = openAdvances[row.id] || [];
          const current = recoveries[row.id] || {};
          const selected = choices.find(
            (advance) => advance.id === current.employeeAdvanceId,
          );
          const maximum = Math.min(
            number(row.netPayable),
            number(selected?.outstandingAmount),
          );
          const afterRecovery = Math.max(
            0,
            number(row.netPayable) - number(current.amount),
          );
          return (
            <div key={row.id} className="p-5 sm:px-6">
              <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{row.name}</p>
                  <p className="text-sm text-gray-500">
                    {row.code} · Estimated payout {money(row.netPayable)}
                  </p>
                </div>
                {current.employeeAdvanceId && current.amount ? (
                  <p className="text-sm font-bold text-primary-800">
                    After recovery: {money(afterRecovery)}
                  </p>
                ) : null}
              </div>
              {choices.length ? (
                <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="mb-1.5 block">Open advance</span>
                    <select
                      value={current.employeeAdvanceId || ""}
                      onChange={(event) =>
                        setValue(row.id, {
                          employeeAdvanceId: event.target.value,
                          amount: "",
                        })
                      }
                      className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                    >
                      <option value="">Do not recover an advance</option>
                      {choices.map((advance) => (
                        <option key={advance.id} value={advance.id}>
                          Issued {date(advance.issuedOn)} ·{" "}
                          {money(advance.outstandingAmount)} outstanding
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    <span className="mb-1.5 block">Recovery amount</span>
                    <input
                      type="number"
                      disabled={!selected}
                      min="0.01"
                      max={maximum || undefined}
                      step="0.01"
                      value={current.amount || ""}
                      onChange={(event) =>
                        setValue(row.id, {
                          ...current,
                          amount: event.target.value,
                        })
                      }
                      placeholder={
                        selected ? `Max ${money(maximum)}` : "Select first"
                      }
                      className="min-h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 disabled:bg-gray-100"
                    />
                  </label>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No open advance is available for this employee.
                </p>
              )}
              {selected ? (
                <p className="mt-2 text-xs text-gray-500">
                  Maximum recovery is {money(maximum)}: the lower of current
                  payout and outstanding advance balance.
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
